import { type ReactElement, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { DeleteOrganisationDialog } from '@/features/organisations/components/dialogs';
import { useProjectFilter } from '@/features/projects';
import { UnauthorizedScreen } from '@/pages/UnauthorizedScreen';
import { useNotifications } from '@/providers/notificationHooks';
import {
  useCurrentOrgRole,
  useDeleteOrganisation,
  useMutationErrorSnackbar,
  useOrganisationDetailQuery,
  useUpdateOrganisation,
} from '@/shared/hooks';
import { iconSizes } from '@/theme/tokens';
import { Box, CircularProgress, Stack, Text } from '@/ui';
import { PageStateScaffold } from '@/ui/composites/scaffolds';
import { useOrganisationFilter } from '../providers/organisationHooks';
import { OrganisationSettingsContent } from './OrganisationSettingsContent';
import type { AuthSession } from '@/auth/types';

type OrganisationSettingsPageProps = { session: AuthSession | null };

export const OrganisationSettingsPage = ({
  session,
}: OrganisationSettingsPageProps): ReactElement => {
  const { selectedOrganisationId, setSelectedOrganisationId } = useOrganisationFilter();
  const mutationError = useMutationErrorSnackbar();
  const { notify } = useNotifications();
  const [nameValue, setNameValue] = useState('');
  const [nameDirty, setNameDirty] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteName, setDeleteName] = useState('');
  const [isDeletingOrg, setIsDeletingOrg] = useState(false);
  const settingsQuery = useOrganisationDetailQuery(selectedOrganisationId);
  const updateOrganisation = useUpdateOrganisation();
  const deleteOrganisation = useDeleteOrganisation();
  const deleteError = useMutationErrorSnackbar();
  const { isAdmin, isLoading: isRoleLoading } = useCurrentOrgRole(session?.userId);
  const settingsData = settingsQuery.data;
  const { setSelectedProjectId } = useProjectFilter();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const deleteMatches = deleteName.trim() === (settingsData?.name ?? '').trim();
  useEffect(() => {
    const data = settingsData;
    if (!data) return;
    setNameValue(data.name);
    setNameDirty(false);
  }, [settingsData?.name]);

  useEffect(() => {
    if (!settingsQuery.error) return;
    if (settingsQuery.error.message !== 'Organisation not found') return;
    setSelectedProjectId(null);
    setSelectedOrganisationId(null);
    void navigate('/');
  }, [navigate, setSelectedOrganisationId, setSelectedProjectId, settingsQuery.error]);

  const isAuthCheckLoading = isRoleLoading && !settingsQuery.isPending;
  const isLoading = settingsQuery.isPending || isAuthCheckLoading;

  return (
    <PageStateScaffold
      session={session}
      sessionMessage="Sign in to view organisation settings."
      organisationId={selectedOrganisationId}
      requireOrganisation
      organisationMessage="Select an organisation to manage settings."
      requireAdmin
      isAdmin={isAdmin}
      adminFallback={
        <UnauthorizedScreen
          title="Unauthorised"
          body="You must be an admin to manage organisation settings."
          backTo="/"
          backLabel="Back to home"
        />
      }
      redirecting={isDeletingOrg}
      redirectMessage="Redirecting to dashboard..."
      isLoading={isLoading}
      loadingFallback={
        <Box
          sx={{
            minHeight: '45vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Stack gap="md" align="center">
            <Text role="body" color="textSecondary" align="center">
              Loading organisation settings...
            </Text>
            <CircularProgress size={iconSizes.lg} />
          </Stack>
        </Box>
      }
      isError={Boolean(settingsQuery.error)}
      error={settingsQuery.error}
    >
      <OrganisationSettingsContent
        organisationId={settingsData?.id ?? null}
        nameValue={nameValue}
        nameDirty={nameDirty}
        disableUpdate={updateOrganisation.isPending}
        onNameChange={(nextValue) => {
          setNameValue(nextValue);
          setNameDirty(nextValue.trim() !== settingsData?.name.trim());
        }}
        onCancel={() => {
          if (!settingsData) return;
          setNameValue(settingsData.name);
          setNameDirty(false);
        }}
        onUpdate={() => {
          if (!settingsData) return;
          updateOrganisation.mutate(
            { id: settingsData.id, name: nameValue },
            {
              onSuccess: (updated) => {
                setNameValue(updated.name);
                setNameDirty(false);
                notify({ message: `Updated organisation ${updated.name}`, tone: 'green' });
                window.dispatchEvent(
                  new CustomEvent('flowqa-org-updated', {
                    detail: { id: updated.id, name: updated.name },
                  }),
                );
              },
              onError: (err) => {
                mutationError.setError(err);
              },
            },
          );
        }}
        onDeleteOpen={() => {
          if (!settingsData) return;
          deleteError.clearError();
          setDeleteName('');
          setDeleteOpen(true);
        }}
      />
      <DeleteOrganisationDialog
        open={deleteOpen}
        organisationName={settingsData?.name ?? ''}
        deleteName={deleteName}
        loading={deleteOrganisation.isPending}
        confirmDisabled={!deleteMatches || !settingsData}
        onDeleteNameChange={(value) => {
          setDeleteName(value);
        }}
        onClose={() => {
          setDeleteOpen(false);
        }}
        onConfirm={() => {
          if (!settingsData || !deleteMatches) return;
          deleteError.clearError();
          setIsDeletingOrg(true);
          deleteOrganisation.mutate(
            { id: settingsData.id },
            {
              onSuccess: () => {
                setDeleteOpen(false);
                notify({ message: `Deleted organisation ${settingsData.name}`, tone: 'blue' });
                window.dispatchEvent(
                  new CustomEvent('flowqa-org-removed', {
                    detail: { reason: 'deleted_by_user' },
                  }),
                );
                signOut({ redirect: false });
                void navigate('/sign-in?manual=1', { replace: true });
              },
              onError: (err) => {
                setIsDeletingOrg(false);
                deleteError.setError(err);
              },
            },
          );
        }}
      />
    </PageStateScaffold>
  );
};
